import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import type { SavedView } from './types'
import { Icon } from '@/components/Icon'

/**
 * Выбор и сохранение представлений списка (ТЗ §5.1).
 *
 * Представления живут на сервере, а не в localStorage: критерий тикета — «представление
 * сохраняется между сессиями», а браузерное хранилище не переживает ни другой компьютер,
 * ни очистку профиля, ни передачу коллеге. Последнее — половина того, ради чего
 * представления и заводят.
 */

interface ViewPickerProps {
  views: SavedView[]
  activeId: string | null
  onApply: (view: SavedView) => void
  onSave: (name: string, shared: boolean) => void
  onUpdate: (id: string) => void
  onDelete: (id: string) => void
  onMakeDefault: (id: string) => void
  /** Изменено ли текущее состояние относительно применённого представления. */
  isDirty: boolean
}

export function ViewPicker({
  views,
  activeId,
  onApply,
  onSave,
  onUpdate,
  onDelete,
  onMakeDefault,
  isDirty,
}: ViewPickerProps) {
  const { t } = useApp()
  const [name, setName] = useState('')
  const [shared, setShared] = useState(false)

  const active = views.find((view) => view.id === activeId) ?? null
  // «Мои» и «Общие» раздельно (дизайн: меню представлений): общее представление — настройка
  // отдела, и его правка — другое действие, чем правка своего.
  const groups: [string, SavedView[]][] = [
    ['view.mine', views.filter((view) => !view.shared)],
    ['view.sharedGroup', views.filter((view) => view.shared)],
  ]

  return (
    <div className="view-picker" role="group" aria-label={t('view.picker')}>
      {views.length === 0 && <div className="menu-label">{t('view.empty')}</div>}
      {groups.map(([labelKey, list]) =>
        list.length === 0 ? null : (
          <div key={labelKey}>
            <div className="menu-label">{t(labelKey)}</div>
            {list.map((view) => (
              <button
                key={view.id}
                type="button"
                className={`menu-item${view.id === activeId ? ' is-active' : ''}`}
                aria-pressed={view.id === activeId}
                onClick={() => onApply(view)}
              >
                {view.id === activeId && <Icon name="check" />}
                {view.name}
                {view.defaultView && <span className="end t-xs muted">{t('view.defaultMark')}</span>}
              </button>
            ))}
          </div>
        ),
      )}

      {/* Кнопка обновления живёт только у своего представления: общее меняет
          настройку, на которую опирается отдел, и это административное действие,
          а не побочный эффект того, что кто-то поправил себе список. */}
      {active && !active.shared && (
        <>
          <div className="menu-sep" />
          <button type="button" className="menu-item" disabled={!isDirty} onClick={() => onUpdate(active.id)}>
            {t('view.update')}
          </button>
          <button type="button" className="menu-item" onClick={() => onMakeDefault(active.id)}>
            {t('view.makeDefault')}
          </button>
          <button type="button" className="menu-item danger" onClick={() => onDelete(active.id)}>
            {t('view.delete')}
          </button>
        </>
      )}

      <div className="menu-sep" />
      <div className="view-save">
        <input
          className="input input-sm"
          aria-label={t('view.name')}
          placeholder={t('view.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <label className="view-shared t-sm">
          <input
            type="checkbox"
            checked={shared}
            onChange={(event) => setShared(event.target.checked)}
          />
          {t('view.shareIt')}
        </label>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={name.trim() === ''}
          onClick={() => {
            onSave(name.trim(), shared)
            setName('')
            setShared(false)
          }}
        >
          {t('view.save')}
        </button>
      </div>
    </div>
  )
}
